/**
 * React Query hooks da base de conhecimento — `/v1/knowledge-entries` (spec 2026-10-08 §2).
 *
 * `enabled` é parâmetro da leitura: o gate da seção é o `DomainSection` do hub
 * (`knowledge-base-author`, `omitWhenBarred`), mas a seção também resolve a cap por conta
 * própria (D-B) e passa aqui — sem a cap, a query nem existe (gatear o FETCH, não só o
 * render: um GET que voltaria 403 é ruído e dispara o callback global de proibido).
 *
 * Toda mutation invalida a RAIZ da lista. A lista vem inteira (o contrato não pagina) e a
 * tela filtra no cliente — por isso há UMA chave de lista, sem filtros nela.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import type {
  CreateKnowledgeEntryBody,
  KnowledgeEntryDto,
  ListKnowledgeEntriesResponse,
  UpdateKnowledgeEntryBody,
} from './types';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

export const KNOWLEDGE_ENTRIES_QUERY_KEY = ['admin', 'knowledge-entries'] as const;

export function useKnowledgeEntries(
  enabled: boolean,
): UseQueryResult<ListKnowledgeEntriesResponse> {
  return useQuery({
    queryKey: KNOWLEDGE_ENTRIES_QUERY_KEY,
    queryFn: () => api.listKnowledgeEntries(),
    enabled,
  });
}

export function useCreateKnowledgeEntry(): UseMutationResult<
  KnowledgeEntryDto,
  Error,
  CreateKnowledgeEntryBody
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateKnowledgeEntryBody) => api.createKnowledgeEntry(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KNOWLEDGE_ENTRIES_QUERY_KEY }),
  });
}

export function useUpdateKnowledgeEntry(): UseMutationResult<
  KnowledgeEntryDto,
  Error,
  { id: string; body: UpdateKnowledgeEntryBody }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateKnowledgeEntryBody }) =>
      api.updateKnowledgeEntry(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KNOWLEDGE_ENTRIES_QUERY_KEY }),
  });
}

export function useDeleteKnowledgeEntry(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteKnowledgeEntry(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KNOWLEDGE_ENTRIES_QUERY_KEY }),
  });
}
